'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Wifi, WifiOff, TrendingUp, Clock } from 'lucide-react';
import { toast } from 'sonner';
import Header from '@/components/Header';
import BottomNav from '@/components/BottomNav';
import BreakingNewsTicker from '@/components/BreakingNewsTicker';
import NewsCard from '@/components/NewsCard';
import CategoryPill from '@/components/CategoryPill';
import ApiSetupBanner from '@/components/ApiSetupBanner';
import { NewsCardSkeleton, HeroCardSkeleton } from '@/components/LoadingSkeleton';
import {
  BREAKING_TICKER_ITEMS,
  CATEGORIES,
  type NewsArticle,
} from '@/lib/mockData';

const CATEGORY_PILLS = [
  { slug: 'all', labelHi: 'सभी', labelEn: 'All', emoji: '📰' },
  { slug: 'jharkhand', labelHi: 'झारखंड', labelEn: 'Jharkhand', emoji: '🏔️' },
  { slug: 'ranchi', labelHi: 'रांची', labelEn: 'Ranchi', emoji: '🏙️' },
  { slug: 'politics', labelHi: 'राजनीति', labelEn: 'Politics', emoji: '🏛️' },
  { slug: 'education', labelHi: 'शिक्षा', labelEn: 'Education', emoji: '📚' },
  { slug: 'jobs', labelHi: 'नौकरी', labelEn: 'Jobs', emoji: '💼' },
  { slug: 'sports', labelHi: 'खेल', labelEn: 'Sports', emoji: '⚽' },
  { slug: 'weather', labelHi: 'मौसम', labelEn: 'Weather', emoji: '🌤️' },
  { slug: 'crime', labelHi: 'अपराध', labelEn: 'Crime', emoji: '🚨' },
  { slug: 'breaking', labelHi: 'ब्रेकिंग', labelEn: 'Breaking', emoji: '🔴' },
];

export default function HomePageClient() {
  const [lang, setLang] = useState<'hi' | 'en'>('hi');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [bookmarks, setBookmarks] = useState<Set<string>>(new Set());
  const [lastUpdated, setLastUpdated] = useState('');
  const [nextRefreshMins, setNextRefreshMins] = useState(60);

  // Load language preference
  useEffect(() => {
    const stored = localStorage.getItem('nj-lang') as 'hi' | 'en' | null;
    if (stored) setLang(stored);
  }, []);

  // Load bookmarks
  useEffect(() => {
    const stored = localStorage.getItem('nj-bookmarks');

    if (stored) {
      try {
        setBookmarks(new Set(JSON.parse(stored)));
      } catch {
        // Ignore invalid bookmark data
      }
    }
  }, []);

  
  // Load live news
  const loadNews = useCallback(async () => {
    try {
      const response = await fetch('/api/news', {
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch news');
      }

      const data = await response.json();

      // Never show old mock articles
      setArticles(
        Array.isArray(data.articles) ? data.articles : []
      );

      setLastUpdated(
        new Date().toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
        })
      );
    } catch (error) {
      console.error('News loading failed:', error);

      // Show no articles if the API fails
      setArticles([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load + automatic hourly refresh
  useEffect(() => {
    loadNews();

    const interval = setInterval(() => {
      loadNews();
      setNextRefreshMins(60);
    }, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [loadNews]);

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      setNextRefreshMins((prev) => {
        if (prev <= 1) return 60;
        return prev - 1;
      });
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const handleLangToggle = useCallback(() => {
    const next = lang === 'hi' ? 'en' : 'hi';

    setLang(next);
    localStorage.setItem('nj-lang', next);
  }, [lang]);

  // Manual refresh
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);

    try {
      const response = await fetch('/api/news');

      if (!response.ok) {
        throw new Error('Failed to refresh news');
      }

      const data = await response.json();

      if (data.articles?.length) {
        setArticles(data.articles);

        setLastUpdated(
          new Date().toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
          })
        );

        setNextRefreshMins(60);

        toast.success(
          lang === 'hi' ? 'खबरें अपडेट हो गईं' : 'News refreshed',
          {
            description:
              lang === 'hi'
                ? 'ताज़ा खबरें लोड हो गई हैं'
                : 'Latest articles loaded',
          }
        );
      } else {
        toast.error(
          lang === 'hi' ? 'नई खबरें नहीं मिलीं' : 'No new articles found'
        );
      }
    } catch (error) {
      console.error('News refresh failed:', error);

      toast.error(
        lang === 'hi'
          ? 'खबरें अपडेट नहीं हो सकीं'
          : 'Could not refresh news'
      );
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, lang]);

  const handleBookmarkToggle = useCallback(
    (id: string) => {
      setBookmarks((prev) => {
        const next = new Set(prev);

        if (next.has(id)) {
          next.delete(id);

          toast.info(
            lang === 'hi' ? 'बुकमार्क हटाया गया' : 'Bookmark removed'
          );
        } else {
          next.add(id);

          toast.success(
            lang === 'hi' ? 'बुकमार्क सेव हो गया' : 'Article bookmarked'
          );
        }

        localStorage.setItem(
          'nj-bookmarks',
          JSON.stringify([...next])
        );

        return next;
      });
    },
    [lang]
  );

  const filteredArticles =
    activeCategory === 'all'
      ? articles
      : activeCategory === 'breaking'
        ? articles.filter((a) => a.isBreaking)
        : articles.filter(
            (a) =>
              a.category === activeCategory ||
              a.district === activeCategory
          );

  const featuredArticle =
    articles.find((a) => a.isFeatured) || articles[0];

  const gridArticles = filteredArticles.filter(
    (a) => a.id !== featuredArticle?.id
  );

  const getCatObj = (slug: string) =>
    CATEGORIES.find((c) => c.slug === slug);

  return (
    <div className="min-h-screen bg-background flex flex-col">
     

      <Header
        lang={lang}
        onLangToggle={handleLangToggle}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        lastUpdated={lastUpdated}
      />

      {/* Breaking News Ticker */}
      <BreakingNewsTicker
        items={BREAKING_TICKER_ITEMS}
        lang={lang}
      />
 {/* Magzter E-Paper Banner */}
      <a
        href="https://www.magzter.com/publishers/Edutech-Ventures-and-Services"
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full bg-gradient-to-r from-red-700 to-red-500 hover:from-red-800 hover:to-red-600 transition-all duration-200 group"
      >
        <div className="max-w-screen-2xl mx-auto px-4 py-3 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <img
                src="/assets/images/image-1789459370717.png"
                alt="Namaste Jharkhand Logo"
                className="h-10 w-auto object-contain drop-shadow"
              />

              <div>
                <p className="text-white/80 text-xs font-semibold uppercase tracking-widest leading-none mb-0.5">
                  {lang === 'hi'
                    ? 'डिजिटल संस्करण'
                    : 'Digital Edition'}
                </p>

                <span className="text-white font-bold text-sm font-hindi">
                  {lang === 'hi'
                    ? 'नमस्ते झारखंड ई-पेपर पढ़ें'
                    : 'Read Namaste Jharkhand E-Paper'}
                </span>
              </div>
            </div>

            <div className="flex-shrink-0 flex items-center gap-1 bg-white text-red-700 text-xs font-bold px-3 py-1.5 rounded-full group-hover:bg-red-50 transition-colors">
              <span className="font-hindi">
                {lang === 'hi'
                  ? 'Magzter पर खोलें'
                  : 'Open on Magzter'}
              </span>
              <span>→</span>
            </div>
          </div>
        </div>
      </a>
      {/* Main content */}
      <main className="flex-1 max-w-screen-2xl mx-auto w-full px-4 py-4 pb-24 lg:pb-6 lg:px-8 xl:px-10">
        <ApiSetupBanner lang={lang} />

        {/* Status bar */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Wifi size={12} className="text-green-500" />

              <span className="font-hindi">
                {lang === 'hi'
                  ? 'लाइव न्यूज़'
                  : 'Live News'}
              </span>
            </div>

            {lastUpdated && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock size={10} />

                <span className="font-hindi">
                  {lang === 'hi'
                    ? `अपडेट: ${lastUpdated}`
                    : `Updated: ${lastUpdated}`}
                </span>
              </div>
            )}
          </div>

          <div className="text-xs text-muted-foreground font-hindi flex items-center gap-1">
            <RefreshCw size={10} />

            {lang === 'hi'
              ? `${nextRefreshMins} मिनट में अपडेट`
              : `Updates in ${nextRefreshMins} min`}
          </div>
        </div>

        {/* Hero Featured Article */}
        {isLoading ? (
          <div className="mb-6">
            <HeroCardSkeleton />
          </div>
        ) : featuredArticle ? (
          <div className="mb-6">
            <NewsCard
              article={featuredArticle}
              lang={lang}
              variant="hero"
              onBookmarkToggle={handleBookmarkToggle}
              isBookmarked={bookmarks.has(featuredArticle.id)}
            />
          </div>
        ) : null}

        {/* Category Pills */}
        <div className="mb-5">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {CATEGORY_PILLS.map((pill) => {
              const catObj = getCatObj(pill.slug);

              return (
                <CategoryPill
                  key={`pill-${pill.slug}`}
                  label={
                    lang === 'hi'
                      ? pill.labelHi
                      : pill.labelEn
                  }
                  emoji={pill.emoji}
                  isActive={activeCategory === pill.slug}
                  onClick={() =>
                    setActiveCategory(pill.slug)
                  }
                  color={catObj?.color}
                  bgColor={catObj?.bgColor}
                />
              );
            })}
          </div>
        </div>

        {/* Section header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp
              size={16}
              className="text-primary"
            />

            <h2 className="font-hindi font-bold text-base text-foreground">
              {lang === 'hi'
                ? 'ताज़ा खबरें'
                : 'Latest News'}
            </h2>

            {!isLoading && (
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                {filteredArticles.length}
              </span>
            )}
          </div>

          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-1.5 text-xs text-primary font-hindi font-medium hover:underline btn-press disabled:opacity-50"
          >
            <RefreshCw
              size={12}
              className={
                isRefreshing ? 'animate-spin' : ''
              }
            />

            {lang === 'hi'
              ? 'रिफ्रेश करें'
              : 'Refresh'}
          </button>
        </div>

        {/* News Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <NewsCardSkeleton
                key={`skeleton-${i + 1}`}
              />
            ))}
          </div>
        ) : gridArticles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4 fade-in">
            {gridArticles.map((article) => (
              <NewsCard
                key={article.id}
                article={article}
                lang={lang}
                variant="default"
                onBookmarkToggle={
                  handleBookmarkToggle
                }
                isBookmarked={bookmarks.has(
                  article.id
                )}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <WifiOff
              size={40}
              className="text-muted-foreground mb-4"
            />

            <h3 className="font-hindi font-bold text-lg text-foreground mb-2">
              {lang === 'hi'
                ? 'कोई खबर नहीं मिली'
                : 'No articles found'}
            </h3>

            <p className="text-muted-foreground text-sm font-hindi max-w-xs">
              {lang === 'hi'
                ? 'इस श्रेणी में अभी कोई खबर उपलब्ध नहीं है। दूसरी श्रेणी चुनें या रिफ्रेश करें।'
                : 'No articles in this category. Try another category or refresh.'}
            </p>

            <button
              onClick={() => setActiveCategory('all')}
              className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-hindi font-medium btn-press"
            >
              {lang === 'hi'
                ? 'सभी खबरें देखें'
                : 'Show All News'}
            </button>
          </div>
        )}
      </main>

      <BottomNav lang={lang} />
    </div>
  );
}